import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Boxes, PackagePlus, Pencil, ScanLine, Trash2 } from 'lucide-react';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { extractErrorMessage } from '@/shared/api/errors';
import { useWarehouseProductsList } from '@/entities/warehouse';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { usePerm } from '@/shared/hooks/usePerm';
import {
  FeaturesWarehouseDeleteDialog,
  FeaturesWarehouseScannerDialog,
} from '@/features/warehouse';
import { formatDate } from '@/shared/utils/date';
import type { ApiWarehouseProductItem } from '@/shared/types/api/warehouse';

const PAGE_SIZE = 20;

/**
 * Tab 2 of the Omborxona page. Lists orphan (unclaimed) warehouse products.
 *
 * Differences vs the previous standalone Egasiz posilkalar page:
 *   - "Status" column is intentionally absent (only UNCLAIMED rows are
 *     shown, so the column would be a constant).
 *   - The hard-coded ``status=UNCLAIMED`` filter scopes the list to "active"
 *     entries per spec.
 */
type Props = {
  scannerOpen: boolean;
  onScannerOpen: () => void;
  onScannerClose: () => void;
};

export const WarehouseOrphanTab = ({ scannerOpen, onScannerOpen, onScannerClose }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const canCreate = usePerm('warehouse.create');
  const canEdit = usePerm('warehouse.edit');
  const canDelete = usePerm('warehouse.delete');
  const canScan = usePerm('warehouse.scan');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);

  const [deleteTarget, setDeleteTarget] = useState<ApiWarehouseProductItem | null>(null);

  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      search: debouncedSearch.trim() || undefined,
      // Active-only per spec: a "linked" ownerless product has already been
      // attached to a parcel and shouldn't pollute the egasiz list.
      status: 'UNCLAIMED' as const,
    }),
    [page, debouncedSearch],
  );

  const query = useWarehouseProductsList(params);

  const columns = useMemo<ColumnDef<ApiWarehouseProductItem>[]>(
    () => [
      {
        accessorKey: 'trek_id',
        header: t('warehouse.list.columns.trekId'),
        cell: (info) => <span className="font-mono text-xs">{info.row.original.trek_id}</span>,
      },
      {
        accessorKey: 'name',
        header: t('warehouse.list.columns.name'),
        cell: (info) => (
          <span className="truncate font-medium text-ink">{info.row.original.name || '—'}</span>
        ),
      },
      {
        accessorKey: 'region',
        header: t('warehouse.list.columns.region'),
        cell: (info) =>
          info.row.original.region ? t(`regions.${info.row.original.region}`) : '—',
      },
      {
        id: 'linked',
        header: t('warehouse.list.columns.linkedParcel'),
        enableSorting: false,
        cell: (info) => {
          const linked = info.row.original.linked_parcel;
          if (!linked) return <span className="text-ink-muted">—</span>;
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/parcels/${linked.id}`);
              }}
              className="font-mono text-xs text-brand-600 hover:underline"
            >
              {linked.trek_id}
            </button>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: t('warehouse.list.columns.date'),
        cell: (info) => formatDate(info.row.original.created_at),
      },
      {
        id: 'actions',
        header: t('warehouse.list.columns.actions'),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex items-center gap-1.5">
              {canEdit && (
                <IconButton
                  label={t('common.edit')}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/warehouse/${row.id}/edit`);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </IconButton>
              )}
              {canDelete && (
                <IconButton
                  label={t('common.delete')}
                  variant="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(row);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </IconButton>
              )}
            </div>
          );
        },
      },
    ],
    [navigate, t, canEdit, canDelete],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <FilterBar
          searchValue={search}
          onSearchChange={(v) => {
            setPage(1);
            setSearch(v);
          }}
          searchPlaceholder={t('warehouse.list.searchPlaceholder')}
        />
        {(canScan || canCreate) && (
          <div className="flex items-center gap-2">
            {canScan && (
              <Button
                variant="outline"
                onClick={onScannerOpen}
                leftIcon={<ScanLine className="h-4 w-4" />}
                title={t('warehouse.list.scan')}
                aria-label={t('warehouse.list.scan')}
              >
                <span className="hidden sm:inline">{t('warehouse.list.scan')}</span>
              </Button>
            )}
            {canCreate && (
              <Button
                onClick={() => navigate('/warehouse/new')}
                leftIcon={<PackagePlus className="h-4 w-4" />}
                title={t('warehouse.list.newProduct')}
                aria-label={t('warehouse.list.newProduct')}
              >
                <span className="hidden sm:inline">{t('warehouse.list.newProduct')}</span>
              </Button>
            )}
          </div>
        )}
      </div>

      {query.error ? (
        <ErrorState
          message={extractErrorMessage(query.error, t('warehouse.list.loadError'))}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <>
          <DataTable
            data={query.data?.results ?? []}
            columns={columns}
            isLoading={query.isLoading}
            emptyTitle={t('warehouse.list.emptyTitle')}
            emptyDescription={t('warehouse.list.emptyDescription')}
            emptyIcon={<Boxes className="h-8 w-8" aria-hidden />}
            getRowId={(row) => row.id}
            numbered
            pageIndex={page - 1}
            pageSize={PAGE_SIZE}
          />
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={query.data?.count ?? 0}
            onPageChange={setPage}
          />
        </>
      )}

      <FeaturesWarehouseScannerDialog open={scannerOpen} onClose={onScannerClose} />
      <FeaturesWarehouseDeleteDialog
        product={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};
